import { CheckIcon, CopyIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  VStack,
} from '@chakra-ui/react';

import Modal from '../../../components/shared/Modal';

type ApiKeySuccessModalProps = {
  isOpen: boolean;
  onClose: () => void;
  newApiKey: string;
  hasUserCopied: boolean;
  handleCopyApiKey: () => void;
};

const ApiKeySuccessModal = ({
  isOpen,
  onClose,
  newApiKey,
  hasUserCopied,
  handleCopyApiKey,
}: ApiKeySuccessModalProps) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="API Key Created Successfully"
      size="lg"
      rightButtons={
        <>
          <Button colorScheme="silverLakeBlue" mr={3} onClick={handleCopyApiKey}>
            {hasUserCopied ? 'Copied!' : 'Copy API Key'}
          </Button>
          <Button colorScheme="lightGreen" onClick={onClose}>
            {hasUserCopied ? 'Close' : 'Close Without Copying'}
          </Button>
        </>
      }
    >
      <VStack spacing={4} align="start" width="100%">
        <Box
          borderRadius="md"
          bg={hasUserCopied ? 'lightGreen.50' : 'taskRed'}
          borderWidth="1px"
          borderColor={hasUserCopied ? 'lightGreen.300' : 'spanishPink'}
          p={4}
          width="100%"
        >
          {hasUserCopied ? (
            <>
              <Heading size="sm" color="lightGreen.600" mb={2} display="flex" alignItems="center">
                <CheckIcon mr={2} />
                API Key Copied Successfully
              </Heading>
              <Text color="lightGreen.600">
                Please make sure to store this API key in a secure location.
              </Text>
            </>
          ) : (
            <>
              <Heading size="sm" color="rubyRed" mb={2}>
                Important: Save This API Key Now
              </Heading>
              <Text color="rubyRed">
                This API key will only be shown once. Please copy and store it securely. You will
                not be able to view it again after closing this window.
              </Text>
            </>
          )}
        </Box>

        <FormControl>
          <FormLabel fontWeight="bold">Your New API Key:</FormLabel>
          <InputGroup size="md">
            <Input
              value={newApiKey}
              isReadOnly
              bg={hasUserCopied ? 'lightGreen.50' : 'gray.100'}
              fontFamily="mono"
              pr="4.5rem"
              borderColor={hasUserCopied ? 'lightGreen.300' : undefined}
            />
            <InputRightElement width="4.5rem">
              <IconButton
                h="1.75rem"
                size="sm"
                aria-label="Copy API key"
                icon={hasUserCopied ? <CheckIcon color="lightGreen.500" /> : <CopyIcon />}
                onClick={handleCopyApiKey}
              />
            </InputRightElement>
          </InputGroup>
        </FormControl>

        <Box>
          <Text fontWeight="bold" mb={1}>
            Instructions:
          </Text>
          <Text>1. Copy this API key and store it in a secure location.</Text>
          <Text>2. If you lose this key, you will need to create a new one.</Text>
          <Text>3. This key grants access to the affiliate API - keep it confidential.</Text>
        </Box>
      </VStack>
    </Modal>
  );
};

export default ApiKeySuccessModal;
